import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, eachDayOfInterval, subDays, startOfDay, endOfDay } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Reading {
  created_at: string;
}

interface ReadingChartProps {
  readings: Reading[];
  days?: number;
}

const ReadingChart: React.FC<ReadingChartProps> = ({ readings, days = 30 }) => {
  const today = new Date();
  const startDate = subDays(today, days - 1);
  
  // Get array of dates for the last N days
  const dateRange = eachDayOfInterval({
    start: startDate,
    end: today
  });

  // Count readings per day
  const readingCounts = dateRange.map(date => {
    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);
    
    return readings.filter(reading => {
      const readingDate = new Date(reading.created_at);
      return readingDate >= dayStart && readingDate <= dayEnd;
    }).length;
  });

  const data = {
    labels: dateRange.map(date => format(date, 'MMM d')),
    datasets: [
      {
        label: 'Readings',
        data: readingCounts,
        fill: true,
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgb(147, 51, 234)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1a1a1a',
        bodyColor: '#1a1a1a',
        borderColor: 'rgba(147, 51, 234, 0.2)',
        borderWidth: 1,
        padding: 10,
        displayColors: false,
        callbacks: {
          title: (tooltipItems: any) => {
            return format(new Date(tooltipItems[0].label), 'MMMM d, yyyy');
          },
          label: (context: any) => {
            return `${context.raw} reading${context.raw !== 1 ? 's' : ''}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0,
        },
        grid: {
          color: 'rgba(147, 51, 234, 0.1)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  };

  return (
    <div className="h-[300px] w-full">
      <Line data={data} options={options} />
    </div>
  );
};

export default ReadingChart;